import { useReducer } from 'react';
import actionTypes from '../constants/action-types';
import { mockedDomainsData } from '../constants/common';
import isDomainSearchQueryValid from '../helpers/is-domain-search-query-valid';
import getDomainPrices from '../helpers/get-domain-prices';

const COM_DOMAIN = 'com';

function useDomainsState(opts) {
  const { domainName, currency } = opts;

  const reducer = (state, action) => {
    switch (action.type) {
      case actionTypes.GET_PRICES:
        return {
          ...state,
          arePricesLoading: true,
        };

      case actionTypes.SET_PRICES: {
        const { prices, extraPrice } = action.payload;
        let comDomainDiscount = null;

        if (extraPrice) {
          const comDomainPrices = getDomainPrices({
            priceData: prices[COM_DOMAIN],
            currency,
            extraPrice,
            isSpecialOfferEnabled: true,
          });
          comDomainDiscount = comDomainPrices.oldPrice - comDomainPrices.price;
        }

        return {
          ...state,
          prices,
          arePricesLoading: false,
          comDomainDiscount,
        };
      }

      case actionTypes.GET_DOMAINS: {
        const { inputValue, areDomainsLoading } = state;

        if (areDomainsLoading) return state;
        if (!isDomainSearchQueryValid(inputValue)) {
          return {
            ...state,
            domainsRequestOptions: null,
            isInputValueValid: false,
          };
        }

        return {
          ...state,
          areDomainsLoading: true,
          domainsRequestOptions: action.payload.options,
          searchString: inputValue,
          // it's important to set small list of domains, in order to avoid UI lags during
          // domains loading if user tries to scroll the list
          domains: mockedDomainsData,
        };
      }

      case actionTypes.SET_DOMAINS: {
        const { domains } = action.payload;
        return {
          ...state,
          domainsRequestOptions: null,
          domains,
          areDomainsLoading: false,
        };
      }

      case actionTypes.GET_TLD_COLLECTION: {
        return {
          ...state,
          isTldCollectionLoading: true,
        };
      }

      case actionTypes.SET_TLD_COLLECTION: {
        const { tldCollection } = action.payload;

        return {
          ...state,
          isTldCollectionLoading: false,
          tldCollection,
        };
      }

      case actionTypes.SET_INPUT_VALUE: {
        const { inputValue: domainInputValue, options } = action.payload;
        const lowerCaseValue = domainInputValue.toLowerCase();

        return {
          ...state,
          inputValue: lowerCaseValue,
          isInputValueValid: Boolean(options.resetValidationResult) || state.isInputValueValid,
        };
      }

      case actionTypes.SET_AUTOCOMPLETE_DATA: {
        const { prefix, options } = action.payload;

        return {
          ...state,
          autocompleteOptions: options,
          autocompleteOptionPrefix: prefix,
        };
      }

      case actionTypes.VALIDATE_VALUE: {
        return {
          ...state,
          isInputValueValid: isDomainSearchQueryValid(state.inputValue),
        };
      }

      default:
        throw new Error('Wrong action type dispatched in B2CDomainSelector');
    }
  };

  const initState = (predefinedDomain) => ({
    prices: null,
    arePricesLoading: false,
    comDomainDiscount: null,
    tldCollection: [],
    isTldCollectionLoading: false,
    domains: isDomainSearchQueryValid(predefinedDomain) ? mockedDomainsData : null,
    areDomainsLoading: false,
    domainsRequestOptions: null,
    searchString: null,
    inputValue: isDomainSearchQueryValid(predefinedDomain) ? predefinedDomain : '',
    isInputValueValid: isDomainSearchQueryValid(predefinedDomain),
    autocompleteOptions: [],
    autocompleteOptionPrefix: '',
  });

  return useReducer(reducer, domainName, initState);
}

export default useDomainsState;
